// Chinese translations for src/config/guide.md.
//
// Keys are the English section titles (used as stable IDs — same string
// passed by tutorials' guideSection option). Each entry may provide `title`
// and/or `body`; missing fields fall back to the English markdown.
import type { ContentBlock } from './index'

export const guideZh: Record<string, ContentBlock> = {
  'Actions, Action Levels, and Runes': {
    title: '行动、行动等级与符文',
    body: `这是游戏中伤害的主要来源。你的角色和敌人在战斗中执行行动来互相造成伤害。持续使用某个行动时，它会根据所造成的伤害获得经验并升级。更高等级的行动会造成额外伤害，速度也略有提升。行动达到过的最高等级还会使行动经验获取速度每级额外提升10%（加法叠加）。行动升级还会解锁符文槽，你可以在其中装配符文来获得行动加成。每个行动有六个符文槽，分别在特定等级解锁：

- **等级 5** — 第1个小型槽
- **等级 10** — 第1个大型槽
- **等级 15** — 第2个小型槽
- **等级 22** — 第3个小型槽
- **等级 30** — 第2个大型槽
- **等级 40** — 关键槽`,
  },

  'Enemy level': {
    title: '敌人等级',
    body: `顶部的敌人等级组件是游戏的主要进程：等级越高，获得的经验越多。你可以手动选择敌人等级，也可以让它自动升级（每波上升1级直至达到上限）。请注意，只有在当前最高等级下游戏时，才能获得通往下一敌人等级的经验。一些新功能也会在更高的敌人等级解锁。
敌人会随等级呈指数级变强，因此你需要利用各层威望系统来获得足够的力量以继续推进。`,
  },

  'Death & Rebirth': {
    title: '死亡与重生',
    body: `当你的生命值归零时，本局结束并触发重生。但你并非完全从头开始：本局获得的所有经验——行动、生命、法力等——都会让你的下一局更强。你现在会根据曾达到的最高等级更快地获得行动、生命和法力经验，并获得精通经验。

如果你感觉进展停滞、无法稳定提升敌人等级，也许是时候触发一次重生，将这些经验兑换成精通带来的大幅强化了。`,
  },

  'Masteries': {
    title: '精通',
    body: `精通是跨越重生持续存在的长期被动升级。每个精通都有自己的经验轨道，通过相关的行动和活动来积累。

精通升级时你会获得一个精通点。打开精通的技能树，用点数购买永久被动加成。Ctrl+点击（或 Cmd+点击）会自动购买通往目标节点路径上所有可负担的节点。

当一个精通的技能树全部购买完毕后，多余的点数可以**转换**为永久加成（大多数精通每点+1%更多，部分为0.5%）。使用精通树面板中的转换按钮；Ctrl+点击（或 Cmd+点击）可一次性转换所有可用点数。`,
  },

  'Action Tags': {
    title: '行动标签',
    body: `每个行动都带有一个**伤害类型标签**：区域、弹射物或打击，以及一个**伤害来源标签**：火焰、闪电或物理。

标签决定哪些精通加成生效以及行动的表现方式——区域攻击命中一个半径范围，弹射物追踪单个目标，打击则贴近近战距离。元素标签决定行动可以施加哪些折磨，以及它受益于哪些元素精通加成。`,
  },

  'Afflictions': {
    title: '折磨',
    body: `折磨是伤害来源（如物理、火焰、闪电）的副产物。命中时以较小的基础概率施加给敌人，并受精通节点影响。

当前的折磨：**燃烧**（火焰）、**流血**（物理）、**电击**（闪电减益）。折磨的持续伤害跳动不算命中——除非明确说明，否则不会触发生命偷取、双倍伤害或状态触发。`,
  },

  'Multi-Action': {
    title: '多重行动',
    body: `多重行动是由主行动或另一个多重行动触发的后续施放。每次施放以正常循环延迟的一部分触发，并可能带有伤害惩罚。

每个多重行动都是一次完整的新施放：它可以触发折磨、触发状态，并判定其他多重行动——但除非明确说明，否则绝不会再次触发产生它的那个多重行动。每一代会叠加 ×0.9 的深度乘数。`,
  },

  'Speed Stockpile': {
    title: '速度储备',
    body: `**×2 速度**消耗你离开期间积累的储备。游戏每停止运行10个真实秒（标签页不活跃、窗口关闭或暂停），你就获得1秒的 ×2 时间，上限为1小时。不足10秒的奖励会被舍弃。

当你回来时，会有通知显示你获得的时间。以 ×2 运行时储备会实时消耗；归零后游戏恢复 ×1，且 ×2 会锁定，直到积累更多储备。`,
  },

  'Ascent': {
    title: '升华',
    body: `升华是在多次重生后解锁的一次性递进挑战。它呈现一系列难度递增的敌人遭遇战。完成一个升华阶段会奖励一个在所有未来重生中持续存在的永久加成——比普通精通进程能提供的任何东西都更强大。

升华进度与你的本局游戏相互独立。你可以随时从菜单尝试；升华阶段失败没有任何代价。`,
  },

  'Transcendence': {
    title: '超越',
    body: `超越是升华之上的声望层级——游戏中最深层的重置，也是最强大的回报。击败敌人等级100或以上的Boss后，金色的**超越**按钮会出现在敌人等级面板底部。无需填充进度条，但每次超越都必须用新的高等级Boss击杀重新获得。

超越会重置升华所重置的一切，外加升华层本身：你的升华次数、宇宙点数以及背包中剩余的神器。你**已装备的神器**和**圣遗物**将保留——而且一旦完成过超越，你的神器装备槽将永久保持解锁。

超越前你要选择一件**圣遗物**——在所有未来重置中永久保留的恩赐。每件圣遗物只能拥有一次：每次超越都会扩充你的收藏，直到全部集齐。

除圣遗物外，每次超越都会永久强化你：经验获取、伤害和最大生命都会随之增长。你的超越力量和收集的圣遗物显示在角色面板底部。

你的首次超越还会解锁**格挡**：你的角色会举起盾牌，获得减轻所受攻击的机会，并在生命与法力区拥有专属的格挡精通——还可以在角色自定义界面中选择盾牌。`,
  },

  'Action Triggers': {
    title: '行动触发器',
    body: `行动触发器会在条件满足时自动发动一个行动。第一个槽位始终是**自动攻击**——它按持续的计时器触发。额外的触发器槽位通过升华解锁。

- **槽位 2** 在升华3解锁。触发器类型：**时间**（周期性计时器）、**法力**（你的行动每消耗100点法力时触发）、**暴击**（在打出暴击后触发——解锁条件：用暴击击杀Boss）、**折磨**（在累积折磨层数后触发——解锁条件：用折磨命中击杀Boss）。
- **槽位 3** 在升华6解锁，触发器选项相同。

每个额外槽位会施加全局伤害惩罚：槽位2为 ×0.75，槽位3为 ×0.50。尽管如此，一个搭配得当的第二行动仍能显著提升总伤害输出。

打开战斗配置面板（位于顶部栏）来更换行动或配置触发器槽位。`,
  },

  'Artifacts': {
    title: '神器',
    body: `神器是达到升华5后由Boss掉落的稀有物品。每件神器都带有一个正面修改器和一个与之配对的负面修改器——一种由你选择接受或拒绝的风险/回报权衡。

击败Boss时，一张掉落卡片会飞向屏幕中央。你可以**收入背包**（留着以后用）、立即**装备**，或直接**丢弃**。

你的背包最多容纳20件神器。装满后，Boss将不再掉落新神器，直到你腾出空间。

**装备槽**随升华进度解锁：
- **升华 5** — 解锁1个装备槽
- **升华 10** — 解锁第2个装备槽

只有已装备的神器才会生效。所有已装备神器的修改器叠加生效。

**稀有度**由神器拥有的修改器对数决定：轻型神器有一对，中型神器有两对，重型神器有三对。更稀有的神器只会在更高的Boss等级开始掉落：
- **Boss等级 30+** — 可掉落中型神器（2行）
- **Boss等级 50+** — 可掉落重型神器（3行）

要管理你的收藏，请从升华菜单打开神器面板（升华5起可用）。在那里你可以装备、卸下或永久删除神器。

**品质** — 每个词条都会显示它与完美数值的接近程度，从 0%（最差）到 100%（完美）；负面词条数值越低评分越高。神器名称旁的百分比是其整体**品质**：所有词条的平均值。

**碎片与升级** — 删除神器（或丢弃刚掉落的神器）会获得**碎片**：轻型 1 个、中型 2 个、重型 3 个。碎片用于**升级**：每次升级随机让一个未达上限的词条提升其品质的 10% —— 正面词条提高，负面词条降低。词条永远不会超过它可能掷出的最佳值。神器的首次升级花费 1 个碎片，之后每次升级费用增长 50%（向上取整）（1、2、3、5、8、12、……）。删除已升级的神器时，除基础价值外还会返还升级所花碎片的一半。

**负面词条移除** — 第 5 次和第 10 次升级会各自先移除品质最差的负面词条，然后照常升级一个词条。重型神器最终只保留一个负面词条，而中型和轻型神器的负面词条会全部消失。完美神器永远不会卡住：只要还有可移除的负面词条，之后的每次升级都会移除一条——只有既完美又没有可移除负面词条的神器才无法继续升级。

**自动丢弃** — 神器面板提供品质阈值设置：低于阈值的新 Boss 掉落会被自动分解为碎片，不会打断战斗。另有独立的重量选择器可按尺寸丢弃（轻型，或中型及以下）——两个条件满足其一即会丢弃。`,
  },
}
